/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useRoute, type ParamListBase } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { CustomerSearchField } from '../../customers/public-api'
import type { CustomerSearchSelection } from '../../customers/public-api'
import { getCustomerAccountById } from '../../customers/public-api';
import { upsertVehicle } from '../infra/vehiclesRepo';
import { validateVehicleDraft } from '../domain/vehicleValidation';
import type { VehicleDraft, VehicleListItem } from '../domain/types';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';

type VehicleFormRouteParams = {
  vehicleId?: string;
  vehicle?: VehicleListItem;
};

function emptyDraft(): VehicleDraft {
  return {
    id: undefined,
    customerId: null,
    plate: '',
    brand: '',
    model: '',
    year: '',
    vin: '',
    color: '',
    mileage: '',
    notes: '',
  };
}

function draftFromListItem(v: VehicleListItem): VehicleDraft {
  return {
    id: v.id,
    customerId: v.customerId,
    plate: v.plate ?? '',
    brand: v.brand ?? '',
    model: v.model ?? '',
    year: v.year != null ? String(v.year) : '',
    vin: v.vin ?? '',
    color: v.color ?? '',
    mileage: v.mileage != null ? String(v.mileage) : '',
    notes: v.notes ?? '',
  };
}

export function VehicleFormScreen() {
  const navigation = useNavigation<NativeStackNavigationProp<ParamListBase>>();
  const route = useRoute();
  const params = route.params as VehicleFormRouteParams | undefined;
  const isEdit = !!params?.vehicleId;
  const { entitlements } = usePlanEntitlements();
  const organizationId = entitlements?.organization_id ?? null;
  const capabilityGate = useCapabilityGate('work_orders', {
    title: 'Vehículos no disponibles',
    subtitle: 'Este rubro no tiene órdenes de trabajo.',
  });

  const [draft, setDraft] = useState<VehicleDraft>(() => {
    if (params?.vehicle) return draftFromListItem(params.vehicle);
    return emptyDraft();
  });
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSearchSelection | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<Exclude<keyof VehicleDraft, 'id'>, string | null>>({
    customerId: null,
    plate: null,
    brand: null,
    model: null,
    year: null,
    vin: null,
    color: null,
    mileage: null,
    notes: null,
  });

  useEffect(() => {
    const cid = draft.customerId;
    if (!cid) return;
    let cancelled = false;
    void (async () => {
      try {
        const account = await getCustomerAccountById(cid);
        if (!cancelled && account) {
          setSelectedCustomer({
            id: account.id,
            name: account.name,
            phone: account.phone || null,
          });
        }
      } catch {
        // keep null — search still works
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolve once on mount/edit
  }, []);

  const setField = <K extends keyof VehicleDraft>(key: K, value: VehicleDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    if (key !== 'id' && errors[key as Exclude<keyof VehicleDraft, 'id'>]) {
      setErrors((e) => ({ ...e, [key]: null }));
    }
  };

  const handleSave = async () => {
    if (!organizationId) {
      showError('Error', 'No se pudo determinar la organización.');
      return;
    }
    const result = validateVehicleDraft(draft);
    if (!result.isValid) {
      setErrors(result.errors);
      const firstError = Object.values(result.errors).find(Boolean);
      showError('Revisá los datos', firstError ?? 'Hay campos con errores.');
      return;
    }
    setSaving(true);
    try {
      await upsertVehicle(organizationId, draft);
      showSuccess(isEdit ? 'Vehículo actualizado' : 'Vehículo creado');
      navigation.goBack();
    } catch (e) {
      showError('Error', e instanceof Error ? e.message : 'No se pudo guardar el vehículo.');
    } finally {
      setSaving(false);
    }
  };

  if (!capabilityGate.allowed) {
    return <ShellContentFrame>{capabilityGate.blocked}</ShellContentFrame>;
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>{isEdit ? 'Editar vehículo' : 'Nuevo vehículo'}</Text>

      <Text style={styles.label}>Patente</Text>
      <TextInput
        style={[styles.input, errors.plate ? styles.inputError : null]}
        value={draft.plate}
        onChangeText={(v) => setField('plate', v)}
        placeholder="AB123CD"
        placeholderTextColor={colors.textSecondary}
        autoCapitalize="characters"
        accessibilityLabel="Patente"
      />
      {errors.plate ? <Text style={styles.errorText}>{errors.plate}</Text> : null}

      <Text style={styles.label}>Marca</Text>
      <TextInput
        style={[styles.input, errors.brand ? styles.inputError : null]}
        value={draft.brand}
        onChangeText={(v) => setField('brand', v)}
        placeholder="Ford"
        placeholderTextColor={colors.textSecondary}
        accessibilityLabel="Marca"
      />
      {errors.brand ? <Text style={styles.errorText}>{errors.brand}</Text> : null}

      <Text style={styles.label}>Modelo</Text>
      <TextInput
        style={[styles.input, errors.model ? styles.inputError : null]}
        value={draft.model}
        onChangeText={(v) => setField('model', v)}
        placeholder="Focus"
        placeholderTextColor={colors.textSecondary}
        accessibilityLabel="Modelo"
      />
      {errors.model ? <Text style={styles.errorText}>{errors.model}</Text> : null}

      <View style={styles.row}>
        <View style={styles.half}>
          <Text style={styles.label}>Año</Text>
          <TextInput
            style={[styles.input, errors.year ? styles.inputError : null]}
            value={draft.year}
            onChangeText={(v) => setField('year', v)}
            placeholder="2018"
            placeholderTextColor={colors.textSecondary}
            keyboardType="numeric"
            accessibilityLabel="Año"
          />
          {errors.year ? <Text style={styles.errorText}>{errors.year}</Text> : null}
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>Color</Text>
          <TextInput
            style={styles.input}
            value={draft.color}
            onChangeText={(v) => setField('color', v)}
            placeholder="Gris"
            placeholderTextColor={colors.textSecondary}
            accessibilityLabel="Color"
          />
        </View>
      </View>

      <Text style={styles.label}>VIN</Text>
      <TextInput
        style={[styles.input, errors.vin ? styles.inputError : null]}
        value={draft.vin}
        onChangeText={(v) => setField('vin', v)}
        placeholder="VIN (opcional)"
        placeholderTextColor={colors.textSecondary}
        autoCapitalize="characters"
        accessibilityLabel="VIN"
      />
      {errors.vin ? <Text style={styles.errorText}>{errors.vin}</Text> : null}

      <Text style={styles.label}>Kilometraje</Text>
      <TextInput
        style={[styles.input, errors.mileage ? styles.inputError : null]}
        value={draft.mileage}
        onChangeText={(v) => setField('mileage', v)}
        placeholder="0"
        placeholderTextColor={colors.textSecondary}
        keyboardType="numeric"
        accessibilityLabel="Kilometraje"
      />
      {errors.mileage ? <Text style={styles.errorText}>{errors.mileage}</Text> : null}

      <Text style={styles.label}>Cliente (opcional)</Text>
      <CustomerSearchField
        selected={selectedCustomer}
        onSelect={(c) => {
          setSelectedCustomer(c);
          setField('customerId', c.id);
        }}
        onClear={() => {
          setSelectedCustomer(null);
          setField('customerId', null);
        }}
        allowFreeText={false}
        testID="vehicle-form.customer-search"
      />

      <Text style={styles.label}>Notas</Text>
      <TextInput
        style={[styles.input, styles.inputMultiline]}
        value={draft.notes}
        onChangeText={(v) => setField('notes', v)}
        placeholder="Notas internas (opcional)"
        placeholderTextColor={colors.textSecondary}
        multiline
        accessibilityLabel="Notas"
      />
      {errors.notes ? <Text style={styles.errorText}>{errors.notes}</Text> : null}

      <View style={styles.actions}>
        <TouchableOpacity
          style={[styles.btn, styles.btnPrimary]}
          onPress={() => void handleSave()}
          disabled={saving}
          accessibilityRole="button"
          accessibilityLabel="Guardar vehículo"
        >
          {saving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.btnPrimaryLabel}>{isEdit ? 'Guardar cambios' : 'Crear vehículo'}</Text>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.btn}
          onPress={() => navigation.goBack()}
          disabled={saving}
          accessibilityRole="button"
          accessibilityLabel="Cancelar"
        >
          <Text style={styles.btnLabel}>Cancelar</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    padding: spacing.md,
    paddingBottom: spacing.xxl,
  },
  title: { ...typography.titleLg, color: colors.textPrimary, fontWeight: '700', marginBottom: spacing.md },
  label: { ...typography.caption, color: colors.textSecondary, fontWeight: '600', marginBottom: spacing.xs, marginTop: spacing.sm },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    color: colors.textPrimary,
  },
  inputError: { borderColor: colors.danger },
  inputMultiline: { minHeight: 80 },
  row: { flexDirection: 'row', gap: spacing.sm },
  half: { flex: 1 },
  errorText: { ...typography.caption, color: colors.danger, marginTop: spacing.xs },
  actions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.lg },
  btn: { flex: 1, paddingVertical: spacing.sm, borderRadius: 10, alignItems: 'center', borderWidth: 1, borderColor: colors.border },
  btnPrimary: { backgroundColor: colors.accent, borderColor: colors.accent },
  btnPrimaryLabel: { ...typography.body, color: '#FFFFFF', fontWeight: '700' },
  btnLabel: { ...typography.body, color: colors.textSecondary },
});
